import {
  CUISINE_LABELS,
  DIETARY_OPTION_LABELS,
  OFFER_INCLUSION_LABELS,
  SERVICE_TYPE_LABELS,
  formatEur,
  type AwardedSupplierContact,
  type OfferComparison,
  type OfferComparisonEntry,
  type OfferInclusions,
  type PriceDisplay,
  type RfqStatus,
} from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@caterinx/ui/components/ui/alert-dialog';
import { Badge } from '@caterinx/ui/components/ui/badge';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Check, Minus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  awardRfq,
  closeRfqEarly,
  fetchOfferComparison,
  fetchRequesterRfq,
  formatDate,
  formatDeadline,
  formatRemaining,
  type RequesterRfqResponse,
} from '../lib/rfq.js';

/**
 * `/anfrage/:rfqId?token=…` — die Statusansicht des Kunden.
 *
 * **Ohne Login, und das ist kein Kompromiss, sondern D6.** Kunden bekommen im MVP kein Konto;
 * der Link aus der Mail *ist* die Anmeldung. Das Token wandert als `X-Share-Token`-Header an
 * die API, nicht als Query-Parameter — der steht sonst in Zugriffsprotokollen und `Referer`.
 *
 * ### Die Frist ist der Kern dieser Seite
 *
 * Sie steht als **Countdown und als konkreter Zeitpunkt** da (D12). Der Countdown tickt lokal
 * jede Sekunde weiter; nachgeladen wird nur, solange die Frist läuft.
 *
 * ### Eingehende Angebote „live" — bewusst per Abfrage statt IoT
 *
 * `05_IMPLEMENTATION_STEPS.md` sieht für diese Stelle den IoT-Push aus Atleo vor. Der ist hier
 * **bewusst nicht gebaut**: Ein Kunde hat im MVP keine Cognito-Identität (D6), also bräuchte
 * der Push unauthentifizierte Identitäten im Identity Pool — ein anonymer Weg zu
 * AWS-Zugangsdaten, nur damit eine Zahl dreißig Sekunden früher erscheint. Die Abfrage alle
 * 30 Sekunden leistet dasselbe, kostet praktisch nichts und öffnet keine neue Tür. Der Preis
 * ist eine halbe Minute Verzögerung, den niemand bemerkt, weil die Angebote über Stunden
 * eintrudeln.
 *
 * ### Der Vergleich
 *
 * Nach Fristablauf stehen die Angebote nebeneinander — mit **Preis pro Gast** als führender
 * Kennzahl und einer Leistungsmatrix. **Ohne Empfehlung**: Der Kunde entscheidet an der
 * Tabelle, nicht an einem KI-Text (`03_AI_CHAT.md` §D). Wer empfiehlt, haftet.
 *
 * ### Und der Ausgang aus der Wartezeit (D23)
 *
 * „Nach Fristablauf" wäre ohne Ausweg eine Falle: Ein Kunde fragt selten nur bei uns an, und
 * eine Frist, die noch neun Stunden läuft, während drei Angebote vorliegen, verliert den
 * Vorgang an den Vermittler nebenan. Deshalb steht hier ab dem ersten Angebot die
 * Schaltfläche **„Angebotsphase jetzt beenden"** — ohne Mindestzahl, ohne Wartebedingung.
 *
 * Was sie trägt, sind die **vier Zahlen** darüber: angeschrieben, Angebote, noch erwartet,
 * abgesagt. Sie sind bewusst der einzige Hebel: Wer sieht, dass noch vier Betriebe rechnen,
 * wartet von selbst — und wer sieht, dass alle geantwortet haben, soll nicht warten müssen.
 * Eine Sperre hätte beides durch eine Regel ersetzt, die in der Hälfte der Fälle danebenliegt.
 *
 * ### Die Preise stehen ab dem ersten Angebot da (Phase 7)
 *
 * Bis dahin zeigte die Seite während der Angebotsphase nur Zahlen: „3 Angebote". Der Kunde
 * bekam die Mail „Erstes Angebot ist da", kam hierher, sah eine Ziffer — und der einzige Weg
 * zu einem Preis führte über **„Angebotsphase jetzt beenden"**. Damit hat die Seite die
 * Neugier gegen die Sache ausgespielt: Der Klick, der die Wartezeit unwiderruflich beendet,
 * war der Klick, mit dem man nachsieht. Wer ihn aus Ungeduld tat, verlor Angebote, die eine
 * Stunde später gekommen wären.
 *
 * Jetzt steht ab dem ersten Eingang {@link LiveOffers} da: Betrieb, Gesamtpreis, Preis pro
 * Person. Kein Grund mehr, zum Nachsehen zu beenden — und die Entscheidung „reicht mir das?"
 * fällt an Preisen statt an einer Ziffer.
 *
 * Was ausdrücklich **nicht** schon dort steht: Positionen, Nachricht des Betriebs, Anlagen.
 * Nicht als Anreiz, sondern weil ein Vergleich, der noch wächst, kein Vergleich ist —
 * nebeneinander gehören die Angebote erst, wenn feststeht, welche es sind.
 *
 * ### Und nach dem Zuschlag: die Übergabe (D24)
 *
 * Zwischen „beauftragt" und „hier sind die Kontaktdaten" liegt seit D24 ein Schritt bei uns.
 * Ohne eigene Karte sähe die Seite in dieser Zeit aus wie ein Fehler — „Auftrag vergeben" und
 * darunter nichts. {@link HandoverPendingCard} sagt stattdessen, was gerade passiert.
 */

const STATUS_TEXT: Record<RfqStatus, { title: string; description: string }> = {
  draft: { title: 'Entwurf', description: 'Diese Anfrage wurde noch nicht abgesendet.' },
  submitted: {
    title: 'Eingegangen — wir prüfen',
    description:
      'Wir sehen uns Ihre Anfrage kurz an und geben sie dann an passende Betriebe in Ihrer Region weiter. Sobald die Ausschreibung heraus ist, erhalten Sie eine E-Mail mit dem genauen Zeitpunkt, bis zu dem die Angebote eingehen.',
  },
  broadcast: {
    title: 'Bei den Anbietern',
    description: 'Die Betriebe kalkulieren. Jedes Angebot erscheint hier mit seinem Preis, sobald es eingeht.',
  },
  closed: {
    title: 'Angebotsphase beendet',
    description: 'Die Frist ist abgelaufen. Sie können die eingegangenen Angebote jetzt vergleichen.',
  },
  awarded: { title: 'Auftrag vergeben', description: 'Sie haben einen Betrieb beauftragt.' },
  completed: { title: 'Abgeschlossen', description: 'Die Leistung wurde erbracht.' },
  cancelled: { title: 'Zurückgezogen', description: 'Diese Anfrage wurde zurückgezogen.' },
  rejected: {
    title: 'Nicht weitergegeben',
    description: 'Wir konnten diese Anfrage nicht ausschreiben. Bei Fragen antworten Sie einfach auf unsere E-Mail.',
  },
  expired: {
    title: 'Kein Angebot eingegangen',
    description:
      'Zu dieser Anfrage ist leider kein Angebot zustande gekommen. Wir melden uns persönlich — und verlängern auf Wunsch die Frist.',
  },
};

export function RfqStatusPage() {
  const { rfqId } = useParams<{ rfqId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: rfq,
    isPending,
    error,
  } = useQuery<RequesterRfqResponse>({
    queryKey: ['rfq', rfqId, token],
    enabled: rfqId !== undefined && token !== '',
    queryFn: () => fetchRequesterRfq(rfqId as string, token),
    /**
     * Nur nachladen, solange etwas passieren kann. Nach Fristablauf ändert sich bis zum
     * Zuschlag nichts mehr — eine Seite, die dann weiter pollt, verbrennt nur Anfragen.
     */
    refetchInterval: (query) => (query.state.data?.status === 'broadcast' ? 30_000 : false),
  });

  const showComparison = rfq?.status === 'closed' || rfq?.status === 'awarded' || rfq?.status === 'completed';

  /**
   * Die eingegangenen Angebote **während** der laufenden Frist.
   *
   * Derselbe Endpunkt wie beim Vergleich — die Matrix erlaubt dem Kunden „eigene Angebote: R
   * (nach Eingang)" ausdrücklich schon vor Fristablauf (`listOffers.ts`). Neu ist nicht die
   * Berechtigung, neu ist, dass die Seite sie nutzt.
   */
  const showLiveOffers = rfq?.status === 'broadcast' && rfq.offerCount > 0;

  /**
   * Ab dem ersten Angebot darf beendet werden — mehr Bedingungen gibt es nicht (D23). Ohne ein
   * einziges Angebot gäbe es nichts zu vergeben; der Server antwortet dort mit 409, und eine
   * Schaltfläche, die nur in eine Fehlermeldung führt, gehört nicht auf die Seite.
   */
  const canCloseEarly = rfq?.status === 'broadcast' && rfq.offerCount > 0;

  const { data: comparison } = useQuery<OfferComparison>({
    queryKey: ['rfq-offers', rfqId, token],
    enabled: (showComparison || showLiveOffers) && rfqId !== undefined && token !== '',
    queryFn: () => fetchOfferComparison(rfqId as string, token),
    /**
     * Solange die Frist läuft, kommen Angebote dazu. Derselbe Takt wie bei der Anfrage
     * selbst — zwei Abfragen im 30-Sekunden-Raster kosten praktisch nichts und ersparen die
     * Erklärung, warum die Zahl oben schon „4" sagt und die Liste noch drei Einträge hat.
     */
    refetchInterval: showLiveOffers ? 30_000 : false,
  });

  /**
   * Der Zuschlag. **Ein Klick, ein Schreibvorgang, keine Rückabwicklung.**
   *
   * Die Bestätigung davor ist kein Zierrat: Mit dem Zuschlag entsteht ein Vertrag zwischen
   * Kunde und Betrieb, beide Kontaktdaten werden freigegeben, und die übrigen Anbieter
   * bekommen ihre Absage. Nichts davon lässt sich zurückholen — die Oberfläche sagt das
   * ausdrücklich, statt es hinter einem Knopf zu verstecken.
   *
   * Ein zweiter Klick ist trotzdem harmlos: Er scheitert serverseitig an der Bedingung der
   * Transaktion (409 `ALREADY_AWARDED`), nicht an dieser Schaltfläche.
   */
  const award = useMutation({
    mutationFn: (supplierId: string) => awardRfq(rfqId as string, supplierId, token),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['rfq', rfqId, token] });
      await queryClient.invalidateQueries({ queryKey: ['rfq-offers', rfqId, token] });
      toast({
        title: 'Auftrag vergeben',
        // Seit D24 stehen die Kontaktdaten **nicht** sofort da. Ein Hinweistext, der etwas
        // anderes behauptet, wäre die erste Enttäuschung nach der Entscheidung.
        description: 'Der Betrieb ist informiert. Wir schließen die Übergabe ab und melden uns mit den Kontaktdaten.',
      });
    },
    onError: (mutationError) => {
      const alreadyAwarded = isAxiosError(mutationError) && mutationError.response?.status === 409;
      toast({
        variant: 'destructive',
        title: alreadyAwarded ? 'Der Zuschlag ist bereits erteilt' : 'Das hat gerade nicht geklappt',
        description: alreadyAwarded
          ? 'Bitte laden Sie die Seite neu — dort steht, an wen der Auftrag gegangen ist.'
          : 'Bitte versuchen Sie es in einem Moment noch einmal.',
      });
    },
  });

  /**
   * Die Angebotsphase vorzeitig beenden (D23). **Nicht rückholbar** — die wartenden Betriebe
   * bekommen in derselben Sekunde ihre Absage, und ein Angebot, das eine Minute später
   * eingegangen wäre, gibt es danach nicht mehr. Deshalb dieselbe Bestätigung wie beim
   * Zuschlag, mit den Zahlen im Text statt eines allgemeinen „Sind Sie sicher?".
   */
  const closeEarly = useMutation({
    mutationFn: () => closeRfqEarly(rfqId as string, token),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['rfq', rfqId, token] });
      await queryClient.invalidateQueries({ queryKey: ['rfq-offers', rfqId, token] });
      toast({
        title: 'Angebotsphase beendet',
        description: 'Die eingegangenen Angebote stehen jetzt nebeneinander — Sie können vergleichen und vergeben.',
      });
    },
    onError: (mutationError) => {
      // 409 heißt hier: Die Frist lief in derselben Minute ab, der Sweeper war schneller. Für
      // den Kunden ist das Ergebnis dasselbe — die Seite muss es nur zeigen.
      const conflict = isAxiosError(mutationError) && mutationError.response?.status === 409;
      toast({
        variant: 'destructive',
        title: conflict ? 'Die Angebotsphase ist bereits beendet' : 'Das hat gerade nicht geklappt',
        description: conflict
          ? 'Bitte laden Sie die Seite neu — die Angebote stehen dann für Sie bereit.'
          : 'Bitte versuchen Sie es in einem Moment noch einmal.',
      });
    },
  });

  if (token === '' || rfqId === undefined) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTitle>Zugangslink unvollständig</AlertTitle>
          <AlertDescription>
            Bitte öffnen Sie den Link aus unserer E-Mail unverändert — er enthält Ihren Zugang.
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  if (isPending) {
    return (
      <Shell>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="mt-4 h-40 w-full" />
      </Shell>
    );
  }

  if (error) {
    // 404 heißt hier auch „falsches Token" — die API bestätigt bewusst nicht, dass es die
    // Anfrage gibt (siehe `authz.ts`). Für den Kunden ist beides derselbe Hinweis.
    const notFound = isAxiosError(error) && error.response?.status === 404;
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertTitle>{notFound ? 'Anfrage nicht gefunden' : 'Das hat gerade nicht geklappt'}</AlertTitle>
          <AlertDescription>
            {notFound
              ? 'Der Link ist ungültig oder gehört zu einer anderen Anfrage. Bitte nutzen Sie den Link aus unserer E-Mail.'
              : 'Bitte laden Sie die Seite in einem Moment neu.'}
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  /*
   * Die Beschriftungstabellen werden mit Enum-Werten indiziert, die serverseitig gegen Zod
   * validiert wurden. Der Object-Injection-Melder kann das nicht sehen.
   */
  /* eslint-disable security/detect-object-injection */
  const status = STATUS_TEXT[rfq.status];
  const { requirements } = rfq;

  /**
   * Derselbe Zustand, anderer Grund — und der Unterschied gehört auf die Seite (D23). „Die
   * Frist ist abgelaufen" wäre falsch, wenn der Kunde vor zwei Minuten selbst beendet hat, und
   * ein Text, der offensichtlich nicht zur eigenen Handlung passt, lässt an allem anderen
   * zweifeln.
   */
  const statusDescription =
    rfq.status === 'closed' && rfq.closedEarly
      ? 'Sie haben die Angebotsphase vorzeitig beendet. Die eingegangenen Angebote stehen unten — die Betriebe, von denen noch nichts vorlag, sind informiert.'
      : status.description;

  return (
    <Shell>
      <Card>
        <CardHeader>
          <CardTitle>{status.title}</CardTitle>
          <CardDescription>{rfq.statusReason ?? statusDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {rfq.status === 'broadcast' && rfq.bidsCloseAt && <Countdown bidsCloseAt={rfq.bidsCloseAt} />}

          {(rfq.status === 'broadcast' || rfq.status === 'closed') && (
            /**
             * **Die vier Zahlen, aus denen die Entscheidung besteht** (D23). „Noch erwartet"
             * ist die wichtigste und stand vorher nicht da: Ohne sie ist „3 Angebote" eine
             * Zahl ohne Maßstab — drei von drei ist fertig, drei von neun ist der halbe Weg.
             */
            <div className="flex flex-wrap gap-6 text-sm">
              <Metric label="Angeschrieben" value={String(rfq.inviteCount)} />
              <Metric label="Angebote" value={String(rfq.offerCount)} highlight />
              {rfq.status === 'broadcast' && <Metric label="Noch erwartet" value={String(rfq.pendingCount)} />}
              {rfq.declinedCount > 0 && <Metric label="Abgesagt" value={String(rfq.declinedCount)} />}
            </div>
          )}

          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <Row label="Termin" value={`${formatDate(requirements.eventDate)}, ${requirements.eventStartTime} Uhr`} />
            <Row label="Gäste" value={String(requirements.guestCount)} />
            <Row label="Ort" value={`${requirements.location.postalCode} ${requirements.location.city}`} />
            <Row
              label="Bewirtung"
              value={requirements.serviceTypes.map((type) => SERVICE_TYPE_LABELS[type]).join(', ')}
            />
            {requirements.eventDurationMinutes !== null && (
              <Row label="Dauer" value={`${Math.round(requirements.eventDurationMinutes / 6) / 10} Stunden`} />
            )}
            {requirements.cuisines.length > 0 && (
              <Row label="Küche" value={requirements.cuisines.map((entry) => CUISINE_LABELS[entry]).join(', ')} />
            )}
            {requirements.dietaryOptions.length > 0 && (
              <Row
                label="Ernährung"
                value={requirements.dietaryOptions
                  .map((option) => {
                    const count = requirements.dietaryCounts[option];
                    return count ? `${DIETARY_OPTION_LABELS[option]} (${count})` : DIETARY_OPTION_LABELS[option];
                  })
                  .join(', ')}
              />
            )}
            {requirements.notes.trim() !== '' && <Row label="Hinweise" value={requirements.notes} full />}
          </dl>
        </CardContent>
      </Card>

      {rfq.supplierContact && <SupplierContactCard contact={rfq.supplierContact} />}

      {/* Vergeben, aber die Übergabe läuft noch (D24). */}
      {(rfq.status === 'awarded' || rfq.status === 'completed') && !rfq.handoverReleased && <HandoverPendingCard />}

      {/* Die Preise während der laufenden Frist — der Grund, nicht aus Neugier zu beenden. */}
      {showLiveOffers && comparison && comparison.offers.length > 0 && (
        <LiveOffers
          offers={comparison.offers}
          pendingCount={rfq.pendingCount}
          prices={pricesFor(comparison.priceDisplay)}
        />
      )}

      {showComparison && comparison && (
        <Comparison
          comparison={comparison}
          awardedSupplierId={rfq.awardedSupplierId}
          canAward={rfq.status === 'closed'}
          isAwarding={award.isPending}
          awardingSupplierId={award.variables ?? null}
          onAward={(supplierId) => award.mutate(supplierId)}
        />
      )}

      {canCloseEarly && (
        <EarlyClose
          offerCount={rfq.offerCount}
          pendingCount={rfq.pendingCount}
          bidsCloseAt={rfq.bidsCloseAt}
          isClosing={closeEarly.isPending}
          onClose={() => closeEarly.mutate()}
        />
      )}

      {rfq.status === 'broadcast' && rfq.offerCount === 0 && rfq.pendingCount > 0 && (
        <p className="text-muted-foreground mt-6 text-sm">
          Sobald das erste Angebot eingeht, benachrichtigen wir Sie per E-Mail — und Sie sehen es hier mit Preis. Die
          Angebotsphase können Sie jederzeit vorzeitig beenden, statt die Frist abzuwarten.
        </p>
      )}

      <p className="text-muted-foreground mt-6 text-xs">
        Stimmt etwas nicht? Antworten Sie einfach auf unsere E-Mail — wir korrigieren das, solange die Anfrage noch
        nicht ausgeschrieben ist.
      </p>
    </Shell>
  );
  /* eslint-enable security/detect-object-injection */
}

/* --------------------------------------------------------------------------------------------
 * Frist
 * ----------------------------------------------------------------------------------------- */

/**
 * Countdown **und** konkreter Zeitpunkt (D12).
 *
 * Der Sekundentakt läuft lokal weiter, ohne Netzverkehr. Der Zeitpunkt daneben ist der
 * eigentliche Inhalt: „noch 3 h 12 min" muss man nachrechnen, „heute um 18:30 Uhr" nicht.
 */
function Countdown({ bidsCloseAt }: { bidsCloseAt: string }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="bg-muted/50 rounded-lg border p-4">
      <p className="text-muted-foreground text-xs">Angebote werden angenommen bis</p>
      <p className="text-lg font-semibold">{formatDeadline(bidsCloseAt)}</p>
      <p className="text-muted-foreground text-sm">{formatRemaining(bidsCloseAt, now)}</p>
    </div>
  );
}

function Metric({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className={highlight ? 'text-xl font-semibold' : 'text-xl'}>{value}</p>
    </div>
  );
}

/* --------------------------------------------------------------------------------------------
 * Vorzeitig beenden (D23)
 * ----------------------------------------------------------------------------------------- */

/**
 * Der Ausgang aus der Wartezeit — **ab dem ersten Angebot, ohne weitere Bedingung**.
 *
 * ### Warum hier keine Sperre steht
 *
 * Naheliegend wäre, erst ab drei Angeboten oder erst bei vollständiger Rückmeldung
 * freizugeben. Das wäre bequem für uns und falsch für den Kunden: Er fragt selten nur bei uns
 * an, und eine Wartezeit, die er nicht abkürzen kann, kostet den ganzen Vorgang an den
 * Vermittler nebenan. Also entscheidet er — und wir geben ihm dafür, was eine Entscheidung
 * braucht.
 *
 * ### Der Text ist die eigentliche Arbeit
 *
 * Beide Zahlen stehen im Fließtext, nicht nur als Kacheln darüber: „drei Angebote, von vier
 * Betrieben erwarten wir noch eines" hält jemanden zurück, der sonst aus Ungeduld klickt. Und
 * wenn niemand mehr aussteht, sagt der Text genau das — dann ist Warten sinnlos, und die
 * Seite soll nicht so tun, als wäre es das nicht.
 *
 * Die Rückfrage im Dialog benennt die **Folge für die anderen**: Wer noch kalkuliert, kommt
 * nicht mehr zum Zug. Das ist keine Warnung, die vom Klicken abhalten soll, sondern die
 * Wahrheit über einen Vorgang, der sich nicht zurücknehmen lässt.
 */
function EarlyClose({
  offerCount,
  pendingCount,
  bidsCloseAt,
  isClosing,
  onClose,
}: {
  offerCount: number;
  pendingCount: number;
  bidsCloseAt: string | null;
  isClosing: boolean;
  onClose: () => void;
}) {
  const offersText = offerCount === 1 ? 'Ein Angebot liegt vor' : `${offerCount} Angebote liegen vor`;

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-lg">Reicht Ihnen der aktuelle Stand?</CardTitle>
        <CardDescription>
          {pendingCount === 0 ? (
            <>
              {offersText}, und alle angeschriebenen Betriebe haben geantwortet. Auf weitere Angebote müssen Sie nicht
              warten — beenden Sie die Angebotsphase und vergleichen Sie jetzt.
            </>
          ) : (
            <>
              {offersText}. Von {pendingCount === 1 ? 'einem weiteren Betrieb' : `${pendingCount} weiteren Betrieben`}{' '}
              erwarten wir noch {pendingCount === 1 ? 'ein Angebot' : 'Angebote'}
              {bidsCloseAt ? <> — angenommen werden sie bis {formatDeadline(bidsCloseAt)}</> : null}. Sie können die
              Angebotsphase aber jederzeit vorzeitig beenden und sofort vergleichen.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" disabled={isClosing}>
              {isClosing ? 'Wird beendet …' : 'Angebotsphase jetzt beenden'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Angebotsphase jetzt beenden?</AlertDialogTitle>
              <AlertDialogDescription>
                Danach stehen Ihnen {offerCount === 1 ? 'das eingegangene Angebot' : `die ${offerCount} Angebote`} zum
                Vergleich bereit und Sie können den Auftrag vergeben.
                {pendingCount > 0 && (
                  <>
                    {' '}
                    {pendingCount === 1 ? 'Ein Betrieb, der' : `${pendingCount} Betriebe, die`} noch kalkuliert
                    {pendingCount === 1 ? '' : 'en'}, {pendingCount === 1 ? 'kann' : 'können'} dann kein Angebot mehr
                    abgeben.
                  </>
                )}{' '}
                Das lässt sich nicht rückgängig machen.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Weiter warten</AlertDialogCancel>
              <AlertDialogAction onClick={onClose}>Beenden und vergleichen</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Eingegangene Angebote während der laufenden Frist (Phase 7)
 * ----------------------------------------------------------------------------------------- */

/**
 * Was schon da ist — **mit Preis**, während die Frist noch läuft.
 *
 * ### Warum das keine Vorwegnahme des Vergleichs ist
 *
 * Der Vergleich unten beantwortet „welches nehme ich?" und braucht dafür Vollständigkeit,
 * Positionen und die Leistungsmatrix. Diese Liste beantwortet eine andere Frage: „bewegt sich
 * etwas, und in welcher Größenordnung?" Dafür genügen Name, Summe und Preis pro Person — und
 * genau das war die Information, für die ein ungeduldiger Kunde bisher die Angebotsphase
 * beenden musste.
 *
 * ### Die Sortierung ist dieselbe wie im Vergleich
 *
 * Nach Preis pro Person, aufsteigend, serverseitig (`sortComparison`). Ein anderer Maßstab
 * hier und dort hätte den Eindruck erzeugt, die Reihenfolge habe sich beim Fristende geändert.
 *
 * ### Kein „günstigstes Angebot"-Abzeichen
 *
 * Es wäre die halbe Wahrheit: Solange noch Angebote kommen, ist „das günstigste" eine Aussage
 * über einen Zwischenstand. Sie stünde eine Stunde später falsch da, und der Kunde hätte
 * seine Entscheidung schon daran gehängt.
 */
function LiveOffers({
  offers,
  pendingCount,
  prices,
}: {
  offers: OfferComparisonEntry[];
  pendingCount: number;
  prices: Prices;
}) {
  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-lg">
          {offers.length === 1 ? 'Ein Angebot ist da' : `${offers.length} Angebote sind da`}
        </CardTitle>
        <CardDescription>
          {prices.allPrices}, sortiert nach Preis pro Person.
          {pendingCount > 0 && (
            <>
              {' '}
              Von {pendingCount === 1 ? 'einem weiteren Betrieb' : `${pendingCount} weiteren Betrieben`} steht die
              Antwort noch aus.
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {offers.map((offer) => (
          <div key={offer.supplierId} className="rounded-lg border p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <div>
                <p className="font-medium">{offer.supplierCompanyName}</p>
                <p className="text-muted-foreground text-sm">{offer.supplierCity}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-semibold">{formatEur(prices.total(offer))}</p>
                <p className="text-muted-foreground text-sm">{formatEur(prices.perGuest(offer))} pro Person</p>
              </div>
            </div>
          </div>
        ))}

        <p className="text-muted-foreground text-xs">
          Die vollständigen Angebote — enthaltene Leistungen, Positionen und Nachrichten der Betriebe — stehen
          nebeneinander, sobald die Angebotsphase beendet ist. Erst dann vergeben Sie den Auftrag.
        </p>
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Zwischen Zuschlag und Übergabe (D24)
 * ----------------------------------------------------------------------------------------- */

/**
 * Der Zustand, den es vor D24 nicht gab: beauftragt, aber die Kontaktdaten sind noch nicht da.
 *
 * ### Was diese Karte leisten muss
 *
 * Der Kunde hat gerade verbindlich beauftragt und sieht **keine** Telefonnummer. Ohne Erklärung
 * ist das der Moment, in dem er sich fragt, ob der Klick überhaupt angekommen ist. Die Karte
 * sagt deshalb dreierlei, in dieser Reihenfolge: Der Auftrag ist angekommen. Der Betrieb weiß
 * Bescheid. Die Kontaktdaten kommen von uns, per E-Mail und hier.
 *
 * ### Was sie ausdrücklich nicht sagt
 *
 * Kein Wort über die Provisionsrechnung. Was caterinx mit dem Betrieb abrechnet, geht den
 * Kunden nichts an (§5, Zeile „Provision / Rechnung") — und „sobald der Betrieb bezahlt hat"
 * wäre ein Zweifel, den er weder prüfen noch auflösen kann.
 *
 * Und keine Zeitangabe in Stunden. Der Schritt hängt an einem Zahlungseingang; ein gerissenes
 * Versprechen wäre schlimmer als gar keins.
 */
function HandoverPendingCard() {
  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">Ihr Auftrag ist bei uns angekommen</CardTitle>
        <CardDescription>Der Betrieb ist informiert. Wir schließen die Übergabe gerade ab.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          Sobald das erledigt ist, erhalten Sie die Kontaktdaten des Betriebs — per E-Mail und hier auf dieser Seite.
          Danach stimmen Sie die Einzelheiten direkt miteinander ab.
        </p>
        <p className="text-muted-foreground text-xs">
          Der Vertrag über die Bewirtung besteht zwischen Ihnen und dem Betrieb; die Rechnung dazu erhalten Sie von
          dort. caterinx vermittelt und rechnet ausschließlich mit dem Betrieb ab — für Sie entstehen keine weiteren
          Kosten.
        </p>
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Angebotsvergleich
 * ----------------------------------------------------------------------------------------- */

/**
 * Netto für Firmen und Organisationen, brutto für Privatpersonen (D38).
 *
 * **Eine Stelle für alle Preise dieser Seite.** Gegenüber Verbrauchern verlangt die
 * Preisangabenverordnung den Gesamtpreis einschließlich Umsatzsteuer — als die Zahl, die ins
 * Auge fällt, nicht als Fußnote. Eine Karte in netto neben einer in brutto wäre schlimmer als
 * beides einheitlich falsch. Die Bruttowerte rechnet der Server (`toComparisonEntry`), damit
 * die Positionen auf den Cent zur Summe passen.
 */
interface Prices {
  gross: boolean;
  total: (offer: OfferComparisonEntry) => number;
  perGuest: (offer: OfferComparisonEntry) => number;
  line: (item: OfferComparisonEntry['lineItems'][number]) => number;
  /** Die jeweils andere Summe, klein daneben. */
  otherTotal: (offer: OfferComparisonEntry) => number;
  allPrices: string;
  totalLabel: string;
  otherLabel: string;
  suffix: string;
}

function pricesFor(priceDisplay: PriceDisplay): Prices {
  const gross = priceDisplay === 'gross';
  return {
    gross,
    total: (offer) => (gross ? offer.total.grossCents : offer.total.netCents),
    perGuest: (offer) => (gross ? offer.grossCentsPerGuest : offer.netCentsPerGuest),
    line: (item) => (gross ? item.grossCents : item.netCents),
    otherTotal: (offer) => (gross ? offer.total.netCents : offer.total.grossCents),
    allPrices: gross ? 'Alle Preise inklusive Umsatzsteuer' : 'Alle Preise netto',
    totalLabel: gross ? 'Gesamtpreis inkl. USt' : 'Gesamtpreis netto',
    otherLabel: gross ? 'Netto' : 'Brutto',
    suffix: gross ? 'inklusive Umsatzsteuer' : 'netto',
  };
}

interface ComparisonProps {
  comparison: OfferComparison;
  awardedSupplierId: string | null;
  /** Nur nach Fristablauf und vor dem Zuschlag. */
  canAward: boolean;
  isAwarding: boolean;
  awardingSupplierId: string | null;
  onAward: (supplierId: string) => void;
}

function Comparison({
  comparison,
  awardedSupplierId,
  canAward,
  isAwarding,
  awardingSupplierId,
  onAward,
}: ComparisonProps) {
  if (comparison.offers.length === 0) {
    return (
      <Alert className="mt-6">
        <AlertTitle>Kein Angebot eingegangen</AlertTitle>
        <AlertDescription>
          Das liegt fast nie am Vorhaben, sondern am Zeitpunkt. Wir melden uns persönlich und sprechen Betriebe direkt
          an — auf Wunsch verlängern wir die Frist. Antworten Sie dazu einfach auf unsere E-Mail.
        </AlertDescription>
      </Alert>
    );
  }

  const inclusionKeys = Object.keys(OFFER_INCLUSION_LABELS) as (keyof OfferInclusions)[];
  const prices = pricesFor(comparison.priceDisplay);

  return (
    <section className="mt-6 space-y-4">
      <div>
        <h2 className="text-xl font-semibold">
          {comparison.offers.length === 1 ? 'Ihr Angebot' : `Ihre ${comparison.offers.length} Angebote`}
        </h2>
        <p className="text-muted-foreground text-sm">
          {awardedSupplierId === null
            ? `${prices.allPrices}, sortiert nach Preis pro Person. Die Kontaktdaten des Betriebs erhalten Sie, sobald Sie sich entschieden haben.`
            : `${prices.allPrices}. Der Auftrag ist vergeben — die Kontaktdaten des beauftragten Betriebs stehen oben.`}
        </p>
      </div>

      {comparison.offers.map((offer, index) => (
        <OfferCard
          key={offer.supplierId}
          offer={offer}
          prices={prices}
          inclusionKeys={inclusionKeys}
          isCheapest={index === 0 && comparison.offers.length > 1}
          isAwarded={offer.supplierId === awardedSupplierId}
          canAward={canAward && awardedSupplierId === null}
          isAwarding={isAwarding && awardingSupplierId === offer.supplierId}
          disabled={isAwarding}
          onAward={() => onAward(offer.supplierId)}
        />
      ))}

      <p className="text-muted-foreground text-xs">
        caterinx vermittelt und spricht bewusst keine Empfehlung aus. Der Preis pro Person und die Übersicht der
        enthaltenen Leistungen sind die belastbaren Vergleichsgrößen; alles Weitere besprechen Sie am besten direkt mit
        dem Betrieb.
      </p>
    </section>
  );
}

/* eslint-disable security/detect-object-injection -- Enum-Schlüssel aus einer festen Tabelle */

function OfferCard({
  offer,
  prices,
  inclusionKeys,
  isCheapest,
  isAwarded,
  canAward,
  isAwarding,
  disabled,
  onAward,
}: {
  offer: OfferComparisonEntry;
  prices: Prices;
  inclusionKeys: (keyof OfferInclusions)[];
  isCheapest: boolean;
  isAwarded: boolean;
  canAward: boolean;
  isAwarding: boolean;
  disabled: boolean;
  onAward: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base">{offer.supplierCompanyName}</CardTitle>
            <CardDescription>{offer.supplierCity}</CardDescription>
          </div>
          {/*
            „Günstigstes Angebot" ist eine **Tatsache**, keine Empfehlung — sie steht ohnehin
            in der Sortierung. Ausdrücklich kein „bestes" oder „empfohlen".
          */}
          <div className="flex flex-wrap gap-2">
            {isCheapest && <Badge variant="secondary">Günstigstes Angebot</Badge>}
            {isAwarded && <Badge>Beauftragt</Badge>}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <div>
            <p className="text-muted-foreground text-xs">{prices.totalLabel}</p>
            <p className="text-2xl font-semibold">{formatEur(prices.total(offer))}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Pro Person</p>
            <p className="text-xl">{formatEur(prices.perGuest(offer))}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">{prices.otherLabel}</p>
            <p className="text-xl">{formatEur(prices.otherTotal(offer))}</p>
          </div>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Enthaltene Leistungen</p>
          <ul className="grid gap-1 text-sm sm:grid-cols-2">
            {inclusionKeys.map((key) => (
              <li key={key} className="flex items-center gap-2">
                {offer.inclusions[key] ? (
                  <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
                ) : (
                  <Minus className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <span className={offer.inclusions[key] ? '' : 'text-muted-foreground'}>
                  {OFFER_INCLUSION_LABELS[key]}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Positionen</p>
          <ul className="space-y-1 text-sm">
            {offer.lineItems.map((item, index) => (
              <li key={`${item.label}-${index}`} className="flex justify-between gap-3">
                <span>
                  {item.quantity} × {item.label}
                  {item.description !== '' && <span className="text-muted-foreground"> — {item.description}</span>}
                </span>
                <span className="shrink-0">{formatEur(prices.line(item))}</span>
              </li>
            ))}
          </ul>
        </div>

        {offer.message.trim() !== '' && (
          <div>
            <p className="mb-1 text-sm font-medium">Nachricht des Betriebs</p>
            <p className="text-muted-foreground text-sm whitespace-pre-wrap">{offer.message}</p>
          </div>
        )}

        {offer.attachmentCount > 0 && (
          <p className="text-muted-foreground text-sm">
            {offer.attachmentCount === 1 ? 'Ein Menüvorschlag' : `${offer.attachmentCount} Menüvorschläge`} liegt bei —
            wir schicken ihn Ihnen mit der Auftragsbestätigung.
          </p>
        )}

        {offer.validUntil && (
          <p className="text-muted-foreground text-xs">
            Der Betrieb bindet sich an dieses Angebot bis {formatDate(offer.validUntil.slice(0, 10))}.
          </p>
        )}

        {canAward && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="w-full sm:w-auto" disabled={disabled}>
                {isAwarding ? 'Wird vergeben …' : `${offer.supplierCompanyName} beauftragen`}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{offer.supplierCompanyName} beauftragen?</AlertDialogTitle>
                {/*
                  Drei Folgen, ausgeschrieben statt angedeutet. Der Zuschlag ist der einzige
                  Vorgang auf dieser Seite, der sich nicht rückgängig machen lässt — und
                  der einzige, der Kontaktdaten in beide Richtungen freigibt (D17).
                */}
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm">
                    <p>
                      Der Auftrag über {formatEur(prices.total(offer))} {prices.suffix} kommt damit direkt zwischen
                      Ihnen und {offer.supplierCompanyName} zustande.
                    </p>
                    <ul className="list-disc space-y-1 pl-5">
                      <li>{offer.supplierCompanyName} wird sofort benachrichtigt.</li>
                      <li>Die übrigen Anbieter erhalten eine Absage.</li>
                      {/*
                        D24: Der Austausch der Kontaktdaten ist ein eigener Schritt bei uns.
                        Er steht hier als dritter Punkt und nicht im Kleingedruckten — ein
                        Kunde, der die Telefonnummer sofort erwartet, ist eine Minute später
                        enttäuscht.
                      */}
                      <li>
                        Die Kontaktdaten tauschen wir aus, sobald wir die Übergabe abgeschlossen haben — Sie erhalten
                        sie per E-Mail und auf dieser Seite.
                      </li>
                    </ul>
                    {/*
                      D38: Vor dem Vertragsschluss muss eine Privatperson erfahren, mit wem sie
                      abschließt und was danach gilt (§ 312l BGB, Art. 246d EGBGB). Firmen
                      kennen das; für sie wäre der Absatz nur Lesestoff.
                    */}
                    {prices.gross && (
                      <p>
                        Alle Betriebe auf caterinx sind geprüfte Unternehmen. {offer.supplierCompanyName} ist Ihr
                        Vertragspartner für Leistung, Rechnung, Änderungen und Stornierung. Bei Catering für einen
                        festen Termin besteht kein gesetzliches Widerrufsrecht (§ 312g Abs. 2 Nr. 9 BGB).
                      </p>
                    )}
                    <p>Das lässt sich nicht rückgängig machen.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={onAward}>Verbindlich beauftragen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Kontaktdaten nach dem Zuschlag
 * ----------------------------------------------------------------------------------------- */

/**
 * Was der Kunde **erst mit dem Zuschlag** sieht (D17).
 *
 * Die Karte steht ganz oben über dem Vergleich, nicht darunter: Nach der Entscheidung ist
 * die nächste Handlung ein Anruf oder eine Mail an den Betrieb, nicht ein weiterer Blick auf
 * die Preise.
 *
 * Der Absatz zur Vermittlerrolle ist keine Floskel — er ist die Stelle, an der die
 * Rollenverteilung zum ersten Mal praktisch wird (`06_BUSINESS_LEGAL.md` §1): Der Vertrag
 * besteht mit dem Betrieb, die Rechnung über die Bewirtung kommt von dort, und caterinx ist
 * nicht Vertragspartei.
 */
function SupplierContactCard({ contact }: { contact: AwardedSupplierContact }) {
  const address = [contact.street, `${contact.postalCode} ${contact.city}`].filter((line) => line).join(', ');

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">Ihr Caterer: {contact.companyName}</CardTitle>
        <CardDescription>Der Betrieb ist informiert und meldet sich zur Abstimmung bei Ihnen.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
          <Row label="Ansprechpartner" value={contact.contactName} />
          <Row label="E-Mail" value={contact.email} />
          {contact.phone && <Row label="Telefon" value={contact.phone} />}
          {address !== '' && <Row label="Anschrift" value={address} />}
        </dl>
        <p className="text-muted-foreground text-xs">
          Der Vertrag über die Bewirtung besteht zwischen Ihnen und {contact.companyName}; die Rechnung dazu erhalten
          Sie von dort. caterinx vermittelt und rechnet ausschließlich mit dem Betrieb ab — für Sie entstehen keine
          weiteren Kosten.
        </p>
      </CardContent>
    </Card>
  );
}

/* eslint-enable security/detect-object-injection */

/* --------------------------------------------------------------------------------------------
 * Rahmen
 * ----------------------------------------------------------------------------------------- */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto w-full max-w-2xl p-4 sm:p-8">
      <Wordmark className="mb-8 w-40" />
      {children}
    </main>
  );
}

function Row({ label, value, full = false }: { label: string; value: string; full?: boolean }) {
  return (
    <div className={full ? 'sm:col-span-2' : undefined}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-sm whitespace-pre-wrap">{value}</dd>
    </div>
  );
}
